import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AtSign, Check, X, Clock, Crown, BadgeCheck, AlertTriangle, Sparkles, History, Shield, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const HandleSettings = ({ user }) => {
  const [newHandle, setNewHandle] = useState('');
  const [checking, setChecking] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [changing, setChanging] = useState(false);
  const [history, setHistory] = useState(null);
  const [showClaimForm, setShowClaimForm] = useState(false);
  const [claimForm, setClaimForm] = useState({
    handle: '',
    company_name: '',
    reason: '',
    verification_document_url: ''
  });
  const [submittingClaim, setSubmittingClaim] = useState(false);
  const [myClaims, setMyClaims] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const isPremium = user?.subscription_tier !== 'basic';
  const isVerified = user?.is_verified;
  const canChangeHandle = isPremium || isVerified;

  useEffect(() => {
    if (canChangeHandle) {
      fetchHistory();
      fetchMyClaims();
    }
  }, [canChangeHandle]);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/handles/history`, { withCredentials: true });
      setHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch handle history');
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchMyClaims = async () => {
    try {
      const response = await axios.get(`${API_URL}/handles/claims`, { withCredentials: true });
      setMyClaims(response.data.claims || []);
    } catch (error) {
      console.error('Failed to fetch claims');
    }
  };

  const checkAvailability = async (handle) => {
    if (!handle || handle.length < 3) {
      setAvailability(null);
      return;
    }
    
    setChecking(true);
    try {
      const response = await axios.get(`${API_URL}/handles/check/${handle}`, { withCredentials: true });
      setAvailability(response.data);
    } catch (error) {
      setAvailability(null);
    } finally {
      setChecking(false);
    }
  };

  // Debounce handle check
  useEffect(() => {
    const timer = setTimeout(() => {
      if (newHandle) {
        checkAvailability(newHandle);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [newHandle]);

  const handleChange = async () => {
    if (!availability?.available) return;
    
    setChanging(true);
    try {
      const response = await axios.post(
        `${API_URL}/handles/change`,
        { new_handle: newHandle },
        { withCredentials: true }
      );
      toast.success(`Handle changed to @${response.data.new_handle}!`);
      setNewHandle('');
      setAvailability(null);
      fetchHistory();
      // Refresh the page to update user context
      setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change handle');
    } finally {
      setChanging(false);
    }
  };

  const submitClaim = async () => {
    if (!claimForm.handle || !claimForm.reason) {
      toast.error('Please fill in the handle and reason');
      return;
    }
    
    setSubmittingClaim(true);
    try {
      await axios.post(
        `${API_URL}/handles/claim`,
        claimForm,
        { withCredentials: true }
      );
      toast.success('Handle claim submitted! Our team will review it.');
      setShowClaimForm(false);
      setClaimForm({ handle: '', company_name: '', reason: '', verification_document_url: '' });
      fetchMyClaims();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit claim');
    } finally {
      setSubmittingClaim(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-700">Pending</span>;
      case 'approved':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-700">Approved</span>;
      case 'rejected':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-700">Rejected</span>;
      default:
        return null;
    }
  };

  if (!canChangeHandle) {
    return (
      <div className="p-6 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-2xl border border-purple-200 dark:border-purple-800">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Crown className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 dark:text-white">Custom Handle</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">Premium feature</p>
          </div>
        </div>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          Upgrade to Premium to choose your own custom @handle. Stand out with a unique username that represents you or your brand.
        </p>
        <Button 
          className="w-full rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold"
          onClick={() => window.location.href = '/pricing'}
          data-testid="upgrade-for-handle-btn"
        >
          <Sparkles className="w-4 h-4 mr-2" />
          Upgrade to Premium
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Current Handle */}
      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-2xl">
        <div className="flex items-center gap-3">
          <AtSign className="w-6 h-6 text-primary" />
          <div>
            <p className="font-semibold text-gray-900 dark:text-white">Current Handle</p>
            <p className="text-lg text-primary font-bold">@{user?.username}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isVerified && (
            <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold">
              <BadgeCheck className="w-4 h-4" />
              Verified
            </span>
          )}
          {isPremium && !isVerified && (
            <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-sm font-semibold">
              <Crown className="w-4 h-4" />
              Premium
            </span>
          )}
        </div>
      </div>

      {/* Cooldown Warning */}
      {!isVerified && history && !history.can_change && (
        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-2xl border border-amber-200 dark:border-amber-800">
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-amber-600" />
            <div>
              <p className="font-semibold text-amber-800 dark:text-amber-200">Handle Change Cooldown</p>
              <p className="text-sm text-amber-700 dark:text-amber-300">
                You can change your handle again in {history.days_until_change} days.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Change Handle Form */}
      {(isVerified || (history && history.can_change)) && (
        <div className="space-y-4">
          <div>
            <Label className="font-semibold text-gray-700 dark:text-gray-200 mb-2 block">
              New Handle
            </Label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">@</span>
              <Input
                value={newHandle}
                onChange={(e) => setNewHandle(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                placeholder="yourhandle"
                className="pl-8 rounded-xl border-2 h-12"
                maxLength={20}
                data-testid="new-handle-input"
              />
              {checking && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              )}
              {!checking && availability && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                  {availability.available ? (
                    <Check className="w-5 h-5 text-green-500" />
                  ) : (
                    <X className="w-5 h-5 text-red-500" />
                  )}
                </div>
              )}
            </div>
            
            {/* Availability Status */}
            <AnimatePresence>
              {availability && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className={`mt-2 p-3 rounded-xl text-sm ${
                    availability.available
                      ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800'
                      : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
                  }`}
                >
                  {availability.available ? (
                    <span className="flex items-center gap-2">
                      <Check className="w-4 h-4" />
                      @{availability.handle} is available!
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <X className="w-4 h-4" />
                      {availability.reason}
                      {availability.is_reserved && (
                        <button
                          onClick={() => {
                            setClaimForm({ ...claimForm, handle: availability.handle });
                            setShowClaimForm(true);
                          }}
                          className="ml-2 underline font-semibold"
                        >
                          Submit a claim
                        </button>
                      )}
                    </span>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <Button
            onClick={handleChange}
            disabled={!availability?.available || changing}
            className="w-full rounded-full bg-primary hover:bg-primary-700 text-white font-bold h-12"
            data-testid="change-handle-btn"
          >
            {changing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Changing...
              </>
            ) : (
              <>
                <AtSign className="w-4 h-4 mr-2" />
                Change Handle
              </>
            )}
          </Button>

          <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
            {isVerified 
              ? 'Verified accounts can change their handle anytime.' 
              : 'Premium users can change their handle once every 30 days.'}
          </p>
        </div>
      )}

      {/* Reserved Handle Claim Form */}
      <AnimatePresence>
        {showClaimForm && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="p-6 bg-blue-50 dark:bg-blue-900/20 rounded-2xl border border-blue-200 dark:border-blue-800"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-blue-900 dark:text-blue-100 flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Claim Reserved Handle
              </h3>
              <button onClick={() => setShowClaimForm(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label className="font-semibold text-gray-700 dark:text-gray-200 mb-2 block">Handle to Claim</Label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">@</span>
                  <Input
                    value={claimForm.handle}
                    onChange={(e) => setClaimForm({ ...claimForm, handle: e.target.value.toLowerCase() })}
                    placeholder="brandname"
                    className="pl-8 rounded-xl border-2 h-12"
                    data-testid="claim-handle-input"
                  />
                </div>
              </div>
              
              <div>
                <Label className="font-semibold text-gray-700 dark:text-gray-200 mb-2 block">Company/Brand Name</Label>
                <Input
                  value={claimForm.company_name}
                  onChange={(e) => setClaimForm({ ...claimForm, company_name: e.target.value })}
                  placeholder="Your Company Inc."
                  className="rounded-xl border-2 h-12"
                  data-testid="claim-company-input"
                />
              </div>
              
              <div>
                <Label className="font-semibold text-gray-700 dark:text-gray-200 mb-2 block">
                  Why should you get this handle? *
                </Label>
                <Textarea
                  value={claimForm.reason}
                  onChange={(e) => setClaimForm({ ...claimForm, reason: e.target.value })}
                  placeholder="I am the official representative of this brand. We own the trademark..."
                  className="rounded-xl border-2 resize-none"
                  rows={3}
                  data-testid="claim-reason-input"
                />
              </div>
              
              <div>
                <Label className="font-semibold text-gray-700 dark:text-gray-200 mb-2 block">
                  Verification Document URL (optional)
                </Label>
                <Input
                  value={claimForm.verification_document_url}
                  onChange={(e) => setClaimForm({ ...claimForm, verification_document_url: e.target.value })}
                  placeholder="https://example.com/trademark-registration.pdf"
                  className="rounded-xl border-2 h-12"
                />
              </div>
              
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    Reserved handles require proof of ownership. Our team will review trademark registrations, 
                    business documents, or other official proof that you represent this entity.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowClaimForm(false)}
                  className="flex-1 rounded-full h-12"
                >
                  Cancel
                </Button>
                <Button
                  onClick={submitClaim}
                  disabled={submittingClaim}
                  className="flex-1 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-bold h-12"
                  data-testid="submit-claim-btn"
                >
                  {submittingClaim ? 'Submitting...' : 'Submit Claim'}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* My Claims */}
      {myClaims.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Your Handle Claims
          </h3>
          {myClaims.map((claim) => (
            <div
              key={claim.claim_id}
              className="p-4 bg-gray-50 dark:bg-gray-700 rounded-xl flex items-center justify-between"
            >
              <div>
                <p className="font-semibold text-gray-900 dark:text-white">@{claim.handle}</p>
                <p className="text-sm text-gray-500">{new Date(claim.created_at).toLocaleDateString()}</p>
              </div>
              <div className="flex flex-col items-end gap-1">
                {getStatusBadge(claim.status)}
                {claim.status === 'rejected' && claim.rejection_reason && (
                  <p className="text-xs text-red-600">{claim.rejection_reason}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Handle History */}
      {history?.history?.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-2">
            <History className="w-5 h-5" />
            Handle History
          </h3>
          <div className="space-y-2">
            {history.history.slice(0, 5).map((entry, idx) => (
              <div
                key={idx}
                className="p-3 bg-gray-50 dark:bg-gray-700 rounded-xl flex items-center justify-between text-sm"
              >
                <span className="text-gray-600 dark:text-gray-300">
                  Changed from <span className="font-semibold">@{entry.old_handle}</span>
                </span>
                <span className="text-gray-500">
                  {new Date(entry.changed_at).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Claim Reserved Handle Button */}
      {!showClaimForm && (
        <div className="pt-4 border-t border-gray-200 dark:border-gray-600">
          <Button
            variant="outline"
            onClick={() => setShowClaimForm(true)}
            className="w-full rounded-full h-12"
            data-testid="open-claim-form-btn"
          >
            <Shield className="w-4 h-4 mr-2" />
            Claim a Reserved Handle (Brand/Trademark)
          </Button>
        </div>
      )}
    </div>
  );
};

export default HandleSettings;
